import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ALLOWED_MIME_TYPES, FILE_INPUT_ACCEPT, isAllowedFile } from '../utils/constants';
import { useUploadManager } from '../contexts/UploadManagerContext';
import { useLanguage } from '../i18n';
import { documentService } from '../services/documentService';
import { logger } from '../utils/logger';
import {
    createStaging, addFiles, removeRow, clearRows, setGlobal, setRowDest, resetRow,
    toUploadPlan, destinationOptions,
} from '../utils/uploadStaging';

// Select values: '' is the root ("My Documents"), anything else a folder id.
const toValue = (dest) => dest || '';

/**
 * Upload picker modal. Files chosen here are handed off to the
 * UploadManager (background) and the modal closes immediately — progress,
 * errors, and completion surface in the HeaderUploadIndicator (next to the
 * conversation search icon). The user can keep working while uploads run,
 * and a persistent reminder is filed in the bell-icon tray on success.
 *
 * Staging table (#395): the picked FILES are listed as a table where each row has
 * its own destination folder. A "Set Global Destination" selector (shown for 2+
 * files) sets every row at once and any row can override it; the default is the
 * folder the File Manager has open (root when none). Folders and archives-as-
 * folders keep their historic behaviour — straight into the open folder. The rules
 * live in `utils/uploadStaging` (unit-tested); this component only renders them.
 */
const DocumentUpload = ({ onUploadSuccess, onUploadComplete, onClose, onCancel, folderId = null, currentFolderLabel = '…' }) => {
    const { t } = useLanguage();
    const [staging, setStaging] = useState(() => createStaging(folderId));
    const selectedFiles = useMemo(() => staging.rows.map((r) => r.file), [staging.rows]);
    // The folder tree feeds the destination selectors (nested, path-labelled).
    const [tree, setTree] = useState([]);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await documentService.getFolderTree();
                if (!cancelled) setTree(res?.folders || []);
            } catch (e) {
                // Non-fatal: the open folder + root stay selectable (see destinationOptions).
                logger.error('UPLOAD', 'Failed to load the folder tree for destinations', e);
            }
        })();
        return () => { cancelled = true; };
    }, []);
    const options = useMemo(
        () => destinationOptions(tree, {
            currentFolderId: folderId,
            rootLabel: t('fmMyDocuments', 'My Documents'),
            currentFolderLabel,
        }),
        [tree, folderId, currentFolderLabel, t],
    );
    const [selectedFolders, setSelectedFolders] = useState([]); // { name: string, files: File[] }
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef();
    const folderInputRef = useRef();
    const { enqueueFiles, enqueueFolder } = useUploadManager();

    const handleFileSelect = (files) => {
        const validFiles = Array.from(files).filter((file) => {
            if (!isAllowedFile(file)) {
                console.warn(`File type not supported: ${file.name} (${file.type})`);
                return false;
            }
            const isArchive = file.name.match(/\.(zip|7z)$/i);
            if (!isArchive && file.size > 1024 * 1024 * 1024) {
                alert(t('fmFileTooLarge', 'File {name} is too large. Maximum size per file is 1GB.', { name: file.name }));
                return false;
            }
            return true;
        });
        setStaging((prev) => addFiles(prev, validFiles));
    };

    // Read all files from a folder entry recursively (used for drag-drop)
    const readFolderEntry = async (entry, basePath = '') => {
        const files = [];
        if (entry.isFile) {
            const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
            const relativePath = basePath ? `${basePath}/${entry.name}` : entry.name;
            files.push({ file, relativePath });
        } else if (entry.isDirectory) {
            const reader = entry.createReader();
            const entries = await new Promise((resolve, reject) => {
                const allEntries = [];
                const readEntries = () => {
                    reader.readEntries((results) => {
                        if (results.length === 0) resolve(allEntries);
                        else { allEntries.push(...results); readEntries(); }
                    }, reject);
                };
                readEntries();
            });
            const newBasePath = basePath ? `${basePath}/${entry.name}` : entry.name;
            for (const childEntry of entries) {
                const childFiles = await readFolderEntry(childEntry, newBasePath);
                files.push(...childFiles);
            }
        }
        return files;
    };

    const handleFolderSelect = async (items) => {
        const newFolders = [];
        for (const item of items) {
            const entry = item.webkitGetAsEntry?.();
            if (entry && entry.isDirectory) {
                try {
                    const folderFiles = await readFolderEntry(entry);
                    const validFiles = folderFiles.filter(({ file }) => isAllowedFile(file));
                    if (validFiles.length > 0) {
                        newFolders.push({
                            name: entry.name,
                            files: validFiles.map(({ file }) => file),
                            filePaths: validFiles.map(({ relativePath }) => relativePath),
                        });
                    }
                } catch (err) {
                    console.error(`Error reading folder ${entry.name}:`, err);
                }
            }
        }
        if (newFolders.length > 0) setSelectedFolders((prev) => [...prev, ...newFolders]);
    };

    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
    const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const items = e.dataTransfer.items;
        const files = [];
        const folderItems = [];
        if (items) {
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                const entry = item.webkitGetAsEntry?.();
                if (entry) {
                    if (entry.isDirectory) folderItems.push(item);
                    else if (entry.isFile) {
                        const file = e.dataTransfer.files[i];
                        if (file) files.push(file);
                    }
                } else {
                    const file = e.dataTransfer.files[i];
                    if (file) files.push(file);
                }
            }
        }
        if (files.length > 0) handleFileSelect(files);
        if (folderItems.length > 0) await handleFolderSelect(folderItems);
    };

    const handleFileInputChange = (e) => {
        if (e.target.files) handleFileSelect(e.target.files);
    };

    const handleFolderInputChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const files = Array.from(e.target.files);
            const folderName = files[0]?.webkitRelativePath?.split('/')[0];
            if (folderName) {
                const validFiles = files.filter((file) => isAllowedFile(file));
                if (validFiles.length > 0) {
                    setSelectedFolders((prev) => [...prev, {
                        name: folderName,
                        files: validFiles,
                        filePaths: validFiles.map((f) => f.webkitRelativePath),
                    }]);
                }
            }
        }
        e.target.value = '';
    };

    const removeFile = (rowId) => setStaging((prev) => removeRow(prev, rowId));
    const removeFolder = (index) => setSelectedFolders((prev) => prev.filter((_, i) => i !== index));

    const startUpload = () => {
        if (selectedFiles.length === 0 && selectedFolders.length === 0) return;

        // Hand each pick off to the UploadManager — background, non-blocking.
        for (const folder of selectedFolders) {
            enqueueFolder(folder, folderId);
        }
        // Each file goes to ITS destination (#395); one enqueue per destination so
        // the upload manager still receives ordinary batches.
        for (const { dest, files } of toUploadPlan(staging)) {
            enqueueFiles(files, dest);
        }

        // Optimistic — close the picker so the user can keep working. Progress
        // and final status surface in the HeaderUploadIndicator (in-header).
        if (onUploadSuccess) {
            onUploadSuccess({ enqueued: selectedFiles.length + selectedFolders.length });
        }
        if (onUploadComplete) onUploadComplete();
        setStaging((prev) => clearRows(prev));
        setSelectedFolders([]);
        onClose?.();
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const totalCount = selectedFiles.length + selectedFolders.length;

    // "Start N folder(s) and M file(s)" — three branches, each atomic phrase
    // translated separately then composed, so the interpolated counts stay
    // numeric (never re-parsed out of a pre-built sentence).
    const startUploadLabel = (() => {
        const nFolders = selectedFolders.length;
        const nFiles = selectedFiles.length;
        let rest;
        if (nFolders > 0 && nFiles > 0) {
            rest = t('fmStartFoldersAndFiles', '{folders} folder(s) and {files} file(s)', { folders: nFolders, files: nFiles });
        } else if (nFolders > 0) {
            rest = t('fmStartFolderUploads', '{n} folder upload{s}', { n: nFolders, s: nFolders === 1 ? '' : 's' });
        } else {
            rest = t('fmStartFileUploads', '{n} file upload{s}', { n: nFiles, s: nFiles === 1 ? '' : 's' });
        }
        return t('fmStartUpload', 'Start {rest}', { rest });
    })();

    return (
        <div
            data-testid="upload-staging-modal"
            data-staged-count={staging.rows.length}
            className="document-upload bg-white/[0.03] rounded-lg shadow-modal-sm max-w-[min(42rem,100%)] mx-auto max-h-[90dvh] min-h-0 flex flex-col"
        >
            <div className="flex justify-between items-center p-6 pb-4 flex-shrink-0">
                <h3 className="text-lg font-semibold text-white">{t('fmUploadDocuments', 'Upload Documents')}</h3>
                {onClose && (
                    <button
                        onClick={onClose}
                        className="text-pai-faint hover:text-white  text-xl"
                    >
                        ×
                    </button>
                )}
            </div>

            <div className="px-6 flex-1 overflow-y-auto">
                <div className="pb-6">

                    {/* Drop Zone */}
                    <div
                        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                            isDragging
                                ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                                : 'border-pai hover:border-pai-strong '
                        }`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <div className="mb-4">
                            <svg className="mx-auto h-12 w-12 text-pai-faint" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <p className="text-pai-secondary mb-2">
                            {t('fmDragDropHere', 'Drag and drop files or folders here, or')}{' '}
                            <button
                                className="text-blue-600 dark:text-blue-400 hover:underline"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {t('fmBrowseFiles', 'browse files')}
                            </button>
                            {' / '}
                            <button
                                className="text-blue-600 dark:text-blue-400 hover:underline"
                                onClick={() => folderInputRef.current?.click()}
                            >
                                {t('fmBrowseFolders', 'browse folders')}
                            </button>
                        </p>
                        <p className="text-sm text-pai-meta">
                            {t('fmSupportsFileTypes', 'Supports documents, source code, config files, data files, and archives')}<br/>
                            <span className="text-xs">{t('fmSizeLimits', 'Individual files up to 1GB. Archives up to 100GB (max 1000 files).')}</span><br/>
                            <span className="text-xs">{t('fmSupportedExtensions', 'PDF, DOCX, PPTX, XLSX, XLS, TXT, MD, PY, JS, TS, HTML, CSS, JSON, CSV, SQL, ZIP, 7Z, and many more...')}</span>
                        </p>
                        <p className="mt-3 text-[11px] text-blue-600 dark:text-blue-400">
                            {t('fmUploadsBackgroundHint', 'Uploads run in the background — you can close this and keep working.')}
                        </p>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept={FILE_INPUT_ACCEPT}
                            onChange={handleFileInputChange}
                            className="hidden"
                        />
                        <input
                            ref={folderInputRef}
                            type="file"
                            webkitdirectory=""
                            directory=""
                            multiple
                            onChange={handleFolderInputChange}
                            className="hidden"
                        />
                    </div>

                    {/* Selected Folders */}
                    {selectedFolders.length > 0 && (
                        <div className="mt-4">
                            <h4 className="font-medium text-pai-secondary mb-2">{t('fmSelectedFolders', 'Selected Folders ({n})', { n: selectedFolders.length })}</h4>
                            <div className="space-y-2">
                                {selectedFolders.map((folder, index) => (
                                    <div key={index} className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 p-3 rounded border border-blue-200 dark:border-blue-700">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                                </svg>
                                                <p className="text-sm font-medium text-white truncate">{folder.name}</p>
                                            </div>
                                            <p className="text-xs text-pai-meta ml-7">
                                                {t('fmFilesToUpload', '{n} file{s} to upload', { n: folder.files.length, s: folder.files.length !== 1 ? 's' : '' })}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => removeFolder(index)}
                                            className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0"
                                        >
                                            {t('commonRemove', 'Remove')}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Staging table (#395) — one row per picked file, each with its
                        own destination; the global selector (2+ files) sets them all. */}
                    {staging.rows.length > 0 && (
                        <div data-testid="upload-staging-table" className="mt-4">
                            <h4 className="font-medium text-pai-secondary mb-2">{t('fmSelectedFiles', 'Selected Files ({n})', { n: staging.rows.length })}</h4>

                            {staging.rows.length >= 2 && (
                                <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded border border-pai bg-white/[0.04] p-3">
                                    <label htmlFor="upload-staging-global" className="text-sm font-medium text-white whitespace-nowrap">
                                        {t('fmUploadStagingGlobal', 'Set Global Destination')}
                                    </label>
                                    <select
                                        id="upload-staging-global"
                                        data-testid="upload-staging-global"
                                        value={toValue(staging.global)}
                                        onChange={(e) => setStaging((prev) => setGlobal(prev, e.target.value))}
                                        className="pai-input h-[38px] min-w-0 flex-1 basis-[12rem] max-w-full px-2 text-[13.5px] text-white"
                                    >
                                        {options.map((o) => (
                                            <option key={o.value || '__root__'} value={toValue(o.value)}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="space-y-2">
                                {staging.rows.map((row) => (
                                    <div
                                        key={row.id}
                                        data-testid="upload-staging-row"
                                        data-filename={row.file.name}
                                        data-overridden={row.overridden ? 'true' : 'false'}
                                        className={`flex flex-wrap items-center gap-x-3 gap-y-2 p-3 rounded ${row.overridden ? 'bg-white/[0.09] ring-1 ring-inset ring-white/25' : 'bg-white/[0.06]'}`}
                                    >
                                        <div className="min-w-0 flex-1 basis-[10rem]">
                                            <p className="text-sm font-medium text-white truncate" title={row.file.name}>{row.file.name}</p>
                                            <p className="text-xs text-pai-meta">
                                                {formatFileSize(row.file.size)} • {ALLOWED_MIME_TYPES[row.file.type] || row.file.type}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2 min-w-0 flex-1 basis-[13rem] max-w-full">
                                            <select
                                                data-testid="upload-staging-destination"
                                                aria-label={t('fmUploadStagingDestinationFor', 'Destination for {name}', { name: row.file.name })}
                                                value={toValue(row.dest)}
                                                onChange={(e) => setStaging((prev) => setRowDest(prev, row.id, e.target.value))}
                                                className="pai-input h-[36px] min-w-0 flex-1 px-2 text-[13px] text-white"
                                            >
                                                {options.map((o) => (
                                                    <option key={o.value || '__root__'} value={toValue(o.value)}>{o.label}</option>
                                                ))}
                                            </select>
                                            {row.overridden && (
                                                <button
                                                    type="button"
                                                    data-testid="upload-staging-reset-row"
                                                    onClick={() => setStaging((prev) => resetRow(prev, row.id))}
                                                    title={t('fmUploadStagingReset', 'Use the global destination')}
                                                    aria-label={t('fmUploadStagingReset', 'Use the global destination')}
                                                    className="flex-shrink-0 text-[11px] font-semibold uppercase tracking-[0.06em] text-pai-meta hover:text-white"
                                                >
                                                    {t('fmUploadStagingCustom', 'Custom')} ↺
                                                </button>
                                            )}
                                        </div>
                                        <button
                                            data-testid="upload-staging-remove"
                                            onClick={() => removeFile(row.id)}
                                            className="text-red-500 hover:text-red-700 flex-shrink-0"
                                        >
                                            {t('commonRemove', 'Remove')}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Fixed Upload Button Section */}
            {totalCount > 0 && (
                <div className="p-6 pt-4 border-t border-pai-subtle bg-white/[0.03] rounded-b-lg flex-shrink-0">
                    <div className="flex flex-wrap gap-3">
                        <button
                            data-testid="upload-staging-start"
                            onClick={startUpload}
                            className="flex-1 basis-[12rem] py-2 px-4 rounded-md font-medium bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            {startUploadLabel}
                        </button>
                        {onCancel && (
                            <button
                                data-testid="upload-staging-cancel"
                                onClick={onCancel}
                                className="px-4 py-2 text-pai-secondary hover:text-white "
                            >
                                {t('commonCancel', 'Cancel')}
                            </button>
                        )}
                        <button
                            onClick={() => {
                                setStaging((prev) => clearRows(prev));
                                setSelectedFolders([]);
                            }}
                            className="px-4 py-2 text-pai-secondary hover:text-white "
                        >
                            {t('fmClearAll', 'Clear All')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentUpload;
